import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getConsent, setConsent, loadGA, loadHotjar } from "./consentManager";
import { useTranslation } from "react-i18next";

const ConsentCtx = createContext(null);
export const useConsent = () => useContext(ConsentCtx);

/**
 * Props:
 *  measurementId: string (GA4)
 *  hotjarId: number
 *  hotjarSv: number
 */
export function ConsentProvider({
  children,
  measurementId,
  hotjarId,
  hotjarSv,
}) {
  const [consent, setConsentState] = useState(() => getConsent());
  const decided = !!consent.decided;

  useEffect(() => {
    // Load tools only when analytics consent is granted
    if (consent.decided && consent.analytics) {
      loadGA({ measurementId });
      loadHotjar({ id: hotjarId, sv: hotjarSv });
    }
  }, [consent.decided, consent.analytics, measurementId, hotjarId, hotjarSv]);

  const acceptAll = () => {
    const next = { decided: true, analytics: true };
    setConsent(next);
    setConsentState(next);
  };
  const rejectNonEssential = () => {
    const next = { decided: true, analytics: false };
    setConsent(next);
    setConsentState(next);
  };

  const value = useMemo(
    () => ({
      consent,
      acceptAll,
      rejectNonEssential,
    }),
    [consent]
  );

  return (
    <ConsentCtx.Provider value={value}>
      {children}
      {!decided && (
        <CookieGate onAccept={acceptAll} onReject={rejectNonEssential} />
      )}
    </ConsentCtx.Provider>
  );
}

/** Full-screen scrim + bottom sheet blocking interactions until decision */
function CookieGate({ onAccept, onReject }) {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/50 bg-opacity-50"
      role="dialog"
      aria-modal="true"
      aria-label={t("consent.title", {
        defaultValue: "Cookies & Analytics",
      })}
    >
      <div className="absolute inset-x-0 bottom-0">
        <div className="mx-auto max-w-lg bg-container rounded-t-2xl p-4 text-greyscale-1 space-y-2">
          <h2 className="title-text">
            {t("consent.title", { defaultValue: "Cookies & Analytics" })}
          </h2>
          <p className="">
            {t("consent.body", {
              defaultValue: "We use essential cookies to run the app. ",
            })}
            <a
              href="https://www.breathe-ultra.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-primary"
              aria-label={t("consent.privacyAria", {
                defaultValue: "Open Privacy Policy in a new tab",
              })}
            >
              {t("consent.privacyLinkText", {
                defaultValue: "Read more",
              })}
            </a>
          </p>
          <div className="flex items-center gap-2 justify-end">
            <button
              className="outlined-button"
              onClick={onReject}
              aria-label={t("consent.rejectAria", {
                defaultValue: "Use essential cookies only",
              })}
            >
              {t("consent.reject", { defaultValue: "Essential only" })}{" "}
            </button>
            <button
              className="primary-button"
              onClick={onAccept}
              aria-label={t("consent.acceptAria", {
                defaultValue: "Accept all cookies",
              })}
            >
              {t("consent.accept", { defaultValue: "Accept all" })}{" "}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
